import { FC, Suspense } from 'react'

import { lazyComponent as lazy } from '$app/utilities'

import { useRouteMeta } from '$actions/useRouteMeta'
import { useRouter } from '$actions/useRouter'

import AccountSelectionGuard from '$guards/AccountSelectionGuard'

import AuthLayout from '$layouts/AuthLayout'
import CenteredLayout from '$layouts/CenteredLayout'
import AuthenticatedLayout from '$layouts/AuthenticatedLayout'

import ProfileWaiter from '$blocks/ProfileWaiter'

import Views from '$views'
import Root from '$views/Root'
import Login from '$views/Login'
import LoginAttentions from '$views/LoginAttentions'
import ForgotPassword from '$views/ForgotPassword'
import ResetPassword from '$views/ResetPassword'
import SmsOtpVerify from '$views/SmsOtpVerify'
import Accounts from '$views/Accounts'
import App from '$views/App'
import InvalidAccess from '$views/InvalidAccess'
import EmailVerification from '$views/EmailVerification'

const ProfileDetail = lazy(() => import('$views/ProfileDetail'))
const MFASetting = lazy(() => import('$views/MFASetting'))
const PasswordSetting = lazy(() => import('$views/PasswordSetting'))
const Applications = lazy(() => import('$views/Applications'))
const LoginHistory = lazy(() => import('$views/LoginHistory'))
const Privacy = lazy(() => import('$views/Privacy'))

export const Routing: FC = () => {
  useRouteMeta()

  const { is404, on, onOneOf } = useRouter()

  const isAuthLayoutWithAuthSelectionGuard = onOneOf([
    'authApp',
    'authLoginAttentions'
  ])
  const isAuthLayout =
    onOneOf([
      'auth',
      'authInvalidAccess',
      'authAccounts',
      'authLogin',
      'authForgotPassword',
      'authResetPassword',
      'authSmsOtpVerify',
      'authEmailVerification',
      'aboutPrivacy'
    ]) || isAuthLayoutWithAuthSelectionGuard

  const isAuthenticatedLayout = onOneOf([
    'applications',
    'profileDetail',
    'securityMfa',
    'securityPassword',
    'securityLoginHistory'
  ])
  const isCenteredLayout = is404 || onOneOf(['root'])

  return (
    <Views>
      {isAuthLayout && (
        <AuthLayout>
          {on('authInvalidAccess') && <InvalidAccess />}

          {on('authLogin') && <Login />}

          {on('authForgotPassword') && <ForgotPassword />}

          {on('authResetPassword') && <ResetPassword />}

          {on('authSmsOtpVerify') && <SmsOtpVerify />}

          {on('authEmailVerification') && <EmailVerification />}

          {isAuthLayoutWithAuthSelectionGuard && (
            <AccountSelectionGuard>
              {on('authLoginAttentions') && (
                <ProfileWaiter preferDisplayMode="data">
                  <LoginAttentions />
                </ProfileWaiter>
              )}

              {on('authApp') && <App />}
            </AccountSelectionGuard>
          )}

          {on('authAccounts') && <Accounts />}

          {on('aboutPrivacy') && (
            <Suspense fallback={<div>Loading...</div>}>
              <Privacy />
            </Suspense>
          )}
        </AuthLayout>
      )}

      {isAuthenticatedLayout && (
        <AccountSelectionGuard>
          <AuthenticatedLayout>
            {on('applications') && <Applications />}

            {on('profileDetail') && <ProfileDetail />}

            {on('securityPassword') && <PasswordSetting />}

            {on('securityMfa') && <MFASetting />}

            {on('securityLoginHistory') && <LoginHistory />}
          </AuthenticatedLayout>
        </AccountSelectionGuard>
      )}

      {isCenteredLayout && (
        <CenteredLayout>
          {on('root') && <Root />}

          {is404 && '404 / Page not found'}
        </CenteredLayout>
      )}
    </Views>
  )
}

export default Routing
